import React, { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { API_BASE as API } from '../../lib/apiBase';

const CorcheaIcon = ({ size = 14, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 3a1 1 0 011 1v10.27A4 4 0 1111 18.13V4a1 1 0 011-1zm0 17a2 2 0 100-4 2 2 0 000 4z"/>
    <path d="M13 4h5v3h-5V4z"/>
  </svg>
);

export function SongSupportButton({ songId, artistId, initialCount = 0, artistName = '' }) {
  const { token, user } = useAuth();
  const [count, setCount] = useState(initialCount);
  const [showMenu, setShowMenu] = useState(false);
  const [loading, setLoading] = useState(false);

  const support = async (amount) => {
    if (!token) { toast.error('Inicia sesión para apoyar'); return; }
    setLoading(true);
    setShowMenu(false);
    try {
      const r = await fetch(`${API}/api/producer/support/song/${songId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount }),
      });
      const data = await r.json();
      if (r.ok) {
        setCount(data.song_corcheas_received || count + amount);
        toast.success(`+${amount} 🎵 enviadas a ${artistName || 'este artista'}`);
      } else {
        toast.error(data.detail || 'Error al apoyar');
      }
    } catch { toast.error('Error de conexión'); }
    finally { setLoading(false); }
  };

  const formatCount = (n) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return n;
  };

  return (
    <div className="relative" data-testid={`song-support-${songId}`}>
      <button
        data-testid="support-btn"
        onClick={() => token ? setShowMenu(!showMenu) : toast.error('Inicia sesión para apoyar')}
        disabled={loading}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#27272A] hover:border-[#F5C842]/50 hover:bg-[#F5C842]/10 transition-all group"
      >
        <CorcheaIcon size={13} className="text-[#A1A1AA] group-hover:text-[#F5C842] transition-colors" />
        <span className="text-xs text-[#A1A1AA] group-hover:text-[#F5C842] font-medium transition-colors">
          {formatCount(count)}
        </span>
      </button>

      {showMenu && (
        <div
          data-testid="support-menu"
          className="absolute bottom-full left-0 mb-2 bg-[#141417] border border-[#27272A] rounded-xl p-2 flex gap-1.5 shadow-xl z-20"
        >
          {[1, 5, 10].map(amount => (
            <button
              key={amount}
              data-testid={`support-amount-${amount}`}
              onClick={() => support(amount)}
              className="px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:bg-[#F5C842]/20 text-[#F5C842] border border-[#F5C842]/30"
            >
              +{amount} 🎵
            </button>
          ))}
        </div>
      )}

      {showMenu && (
        <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
      )}
    </div>
  );
}

export function ArtistSupportButton({ artistId, initialCount = 0, artistName = '' }) {
  const { token } = useAuth();
  const [count, setCount] = useState(initialCount);
  const [showMenu, setShowMenu] = useState(false);
  const [loading, setLoading] = useState(false);

  const support = async (amount) => {
    if (!token) { toast.error('Inicia sesión para apoyar'); return; }
    setLoading(true);
    setShowMenu(false);
    try {
      const r = await fetch(`${API}/api/producer/support/artist/${artistId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount }),
      });
      const data = await r.json();
      if (r.ok) {
        setCount(data.artist_corcheas_received || count + amount);
        toast.success(`+${amount} 🎵 enviadas a ${artistName}`);
      } else {
        toast.error(data.detail || 'Error al apoyar');
      }
    } catch { toast.error('Error de conexión'); }
    finally { setLoading(false); }
  };

  const formatCount = (n) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return n;
  };

  return (
    <div className="relative" data-testid={`artist-support-${artistId}`}>
      <button
        data-testid="artist-support-btn"
        onClick={() => token ? setShowMenu(!showMenu) : toast.error('Inicia sesión')}
        disabled={loading}
        className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#F5C842]/40 bg-[#F5C842]/10 hover:bg-[#F5C842]/20 transition-all"
      >
        <CorcheaIcon size={14} className="text-[#F5C842]" />
        <span className="text-sm font-semibold text-[#F5C842]">
          {formatCount(count)} · Apoyar
        </span>
      </button>

      {showMenu && (
        <div
          data-testid="artist-support-menu"
          className="absolute top-full left-0 mt-2 bg-[#141417] border border-[#27272A] rounded-xl p-2 flex gap-2 shadow-xl z-20"
        >
          {[1, 5, 10].map(amount => (
            <button
              key={amount}
              data-testid={`artist-support-amount-${amount}`}
              onClick={() => support(amount)}
              className="px-4 py-2.5 rounded-xl text-sm font-bold transition-all hover:bg-[#F5C842]/20 text-[#F5C842] border border-[#F5C842]/30"
            >
              +{amount} 🎵
            </button>
          ))}
        </div>
      )}

      {showMenu && (
        <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
      )}
    </div>
  );
}
